import React from "react";

import { isModalDialogOpen } from "#app/lib/hooks/use-hotkey";
import { isTextEditingTarget } from "#app/lib/utils/typing-target";

/** Keycap for the bare `/` that focuses the current view's search — the
 *  tooltip on whichever search is the target advertises it, and the shortcuts
 *  reference derives its row from it. */
export const VIEW_SEARCH_SHORTCUT: readonly string[] = ["/"];

/**
 * A search `/` can land in: a focusable element, or a handle that opens a
 * collapsed field and reports where it sits (`ExpandableSearchHandle`).
 */
type SlashSearchTarget =
  | HTMLElement
  | { focus: () => void; readonly element: HTMLElement | null };

type Registration = {
  ref: React.RefObject<SlashSearchTarget | null>;
};

// ONE `/` owner for the whole app. Every search that wants the key registers
// here; the one furthest along in document order is the target. That order
// is the arbitration: a view's toolbar search sits after the settings sidebar
// in the DOM, so it wins on a settings screen that has both; a dialog's
// search portals to the end of <body>, so it wins while the dialog is open
// and hands the key back when it closes. No per-screen rules.
const registrations = new Set<Registration>();
const subscribers = new Set<() => void>();
let current: Registration | null = null;

function elementOf(target: SlashSearchTarget | null): HTMLElement | null {
  if (!target) return null;
  return target instanceof HTMLElement ? target : target.element;
}

function resolveCurrent(): Registration | null {
  let winner: Registration | null = null;
  let winnerElement: HTMLElement | null = null;
  for (const registration of registrations) {
    const element = elementOf(registration.ref.current);
    if (!element) continue;
    if (
      !winnerElement ||
      winnerElement.compareDocumentPosition(element) &
        Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      winner = registration;
      winnerElement = element;
    }
  }
  return winner;
}

function refresh(): void {
  current = resolveCurrent();
  for (const notify of subscribers) notify();
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

// `e.key`, not `e.code`: with no modifier the chord follows the viewer's
// layout, and on layouts where the slash needs Shift the Shift is part of
// typing it, so Shift is not a disqualifier. preventDefault keeps Firefox's
// quick-find from claiming the key.
function handleKeyDown(e: KeyboardEvent): void {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (e.key !== "/") return;
  if (e.defaultPrevented) return;
  // Typing owns the character; a menu, listbox or combobox owns it as
  // typeahead.
  if (isTextEditingTarget(e.target)) return;
  const origin = e.target instanceof Element ? e.target : null;
  if (origin?.closest('[role="menu"],[role="listbox"],[role="combobox"]')) return;

  // Resolved at press time, not from the cached `current`: a target's element
  // can appear after its registration (a popup that mounts its content a
  // frame late), and the cached value only feeds the tooltips.
  const winner = resolveCurrent();
  const winnerElement = winner ? elementOf(winner.ref.current) : null;
  if (!winner || !winnerElement) return;

  // A dialog owns the key while it is open, unless the target is the
  // dialog's own search — the key must never reach a search behind a modal,
  // whether focus is inside the modal or fell to <body>.
  const winnerDialog = winnerElement.closest('[role="dialog"]');
  const originDialog = origin?.closest('[role="dialog"]') ?? null;
  if (originDialog && originDialog !== winnerDialog) return;
  if (!winnerDialog && isModalDialogOpen()) {
    return;
  }

  e.preventDefault();
  winner.ref.current?.focus();
}

function register(registration: Registration): () => void {
  registrations.add(registration);
  if (registrations.size === 1) window.addEventListener("keydown", handleKeyDown);
  refresh();
  return () => {
    registrations.delete(registration);
    if (registrations.size === 0) {
      window.removeEventListener("keydown", handleKeyDown);
    }
    refresh();
  };
}

/**
 * Registers `ref` as a place the bare `/` hotkey can land while the caller is
 * mounted (and `enabled`). Returns whether it is the CURRENT target — the one
 * `/` would focus right now — so the caller shows the keycap only when it is
 * true. Exactly one search per view registers; a screen that renders several
 * (widgets on a dashboard) registers none, since `/` cannot mean all of them.
 */
export function useSlashSearchTarget(
  ref: React.RefObject<SlashSearchTarget | null>,
  enabled = true,
): boolean {
  const registration = React.useMemo<Registration>(() => ({ ref }), [ref]);

  React.useEffect(() => {
    if (!enabled) return;
    return register(registration);
  }, [registration, enabled]);

  const isCurrent = React.useSyncExternalStore(
    subscribe,
    () => current === registration,
    () => false,
  );
  return enabled && isCurrent;
}
